import { X, AlertTriangle, RefreshCw, ServerCrash, ShieldAlert, WifiOff } from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '@/services/api';

interface ApiErrorModalProps {
  error: ApiError;
  onDismiss: () => void;
  onRetry?: () => void;
}

const ERROR_META: Record<string, { icon: typeof AlertTriangle; title: string; hint: string }> = {
  SandboxPlacementUnavailable: {
    icon: ServerCrash,
    title: 'No Capacity Available',
    hint: 'The pool has no sandbox capacity right now. Try again shortly or select a different pool.',
  },
  IdempotencyConflict: {
    icon: ShieldAlert,
    title: 'Conflict',
    hint: 'A sandbox with this name already exists but with different configuration. Use a unique name.',
  },
};

function getErrorMeta(error: ApiError) {
  if (error.code && ERROR_META[error.code]) {
    return ERROR_META[error.code];
  }
  if (error.status === 401 || error.status === 403) {
    return {
      icon: ShieldAlert,
      title: error.status === 401 ? 'Authentication Required' : 'Access Denied',
      hint: error.status === 401
        ? 'Your session may have expired. Try signing out and back in.'
        : 'You do not have the required role to perform this action.',
    };
  }
  if (error.status === 0 || error.detail === 'Failed to fetch') {
    return {
      icon: WifiOff,
      title: 'Connection Failed',
      hint: 'Could not reach the service. Check that the Vite proxy and ManagementService URL are configured correctly.',
    };
  }
  return {
    icon: AlertTriangle,
    title: `Error ${error.status}`,
    hint: 'An unexpected error occurred. Check the browser console for details.',
  };
}

export function ApiErrorModal({ error, onDismiss, onRetry }: ApiErrorModalProps) {
  const meta = getErrorMeta(error);
  const Icon = meta.icon;

  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div
        className="modal max-w-md flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent strip */}
        <div className="h-1 rounded-t-fluent-lg bg-gradient-to-r from-red-500 via-red-400 to-orange-400" />

        {/* Close button */}
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 btn-subtle p-1.5"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Body */}
        <div className="px-6 pt-6 pb-2">
          {/* Icon */}
          <div className={clsx(
            'w-12 h-12 rounded-fluent-lg flex items-center justify-center mb-4',
            'bg-red-500/10 dark:bg-red-500/15',
          )}>
            <Icon className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-surface-900 dark:text-surface-100 mb-1">
            {meta.title}
          </h3>

          {/* Error message from API */}
          <p className="text-sm text-surface-600 dark:text-surface-300 leading-relaxed mb-3">
            {error.detail}
          </p>

          {/* Hint */}
          <div className={clsx(
            'rounded-fluent p-3 text-xs leading-relaxed',
            'bg-surface-100 dark:bg-white/[0.04]',
            'text-surface-500 dark:text-surface-400',
          )}>
            {meta.hint}
          </div>

          {/* Error code badge */}
          {error.code && (
            <div className="mt-3">
              <span className={clsx(
                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono',
                'bg-red-500/8 dark:bg-red-500/10 text-red-600 dark:text-red-400',
                'border border-red-500/15 dark:border-red-500/20',
              )}>
                {error.code}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <button type="button" onClick={onDismiss} className="btn-secondary">
            Dismiss
          </button>
          {onRetry && (
            <button type="button" onClick={onRetry} className="btn-primary">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
